import { css } from '@emotion/react';
import styled from '@emotion/styled';
import * as Icons from '@polestar/component-warehouse-react/tokens/icons/Icons';
import Opacities from '@polestar/component-warehouse-react/tokens/opacities/Opacities';
import React, { forwardRef, useCallback } from 'react';
import IconButton, { IconButtonVariants, } from '@polestar/component-warehouse-react/components/icon-button/IconButton';
import Spacings, { createCustomSpacing, } from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import aria from '@polestar/component-warehouse-react/helpers/accessibility/aria';
import Input from '@polestar/component-warehouse-react/components/input/Input';
import { useFocusTriggers } from '@polestar/component-warehouse-react/hooks/useFocusTriggers';
import { shouldNotForwardProps } from '@polestar/component-warehouse-react/helpers/shouldForwardProps';
import { hasErrorValue } from '@polestar/component-warehouse-react/components/form-field/FormField';
const NumberStepperButton = ({ onClick, disabled, icon, ariaLabel, ...props }) => (React.createElement(StyledIconButton, { ...aria({
        disabled,
        role: 'button',
        label: ariaLabel,
    }), disabled: disabled, onClick: disabled ? undefined : onClick, icon: icon, pairedWithText: true, variant: IconButtonVariants.transparent, ...props }));
const StyledIconButton = styled(IconButton, shouldNotForwardProps(false, 'error'))(({ theme }) => css `
    color: ${theme.numberStepper.states.enabled};

    &:hover {
      color: ${theme.numberStepper.states.hover};
    }
    &:active {
      color: ${theme.numberStepper.states.active};
    }
  `);
const NumberStepper = forwardRef(({ id, onChange, onFocus, onBlur, max, value, name, min = 0, disabled = false, hasLine = true, errorMessage, error = false, inputProps, ariaPlusButtonLabel, ariaMinusButtonLabel, ...props }, ref) => {
    const hasError = hasErrorValue(error, errorMessage);
    const canIncrement = !disabled && (!max || (value ? value < max : true));
    const canDecrement = !disabled && (value ? value > min : false);
    const handleChange = useCallback((event) => {
        const inputValue = event.target.value;
        const val = Number(inputValue);
        if (!isNaN(val)) {
            // ensure that `02` becomes `2` and ensure only numbers are allowed
            onChange === null || onChange === void 0 ? void 0 : onChange(inputValue === '' ? undefined : val);
        }
    }, [onChange]);
    const handleDecrement = useCallback(() => {
        if (value) {
            const newValue = value - 1;
            onChange === null || onChange === void 0 ? void 0 : onChange(Math.max(newValue, min));
        }
    }, [min, onChange, value]);
    const handleIncrement = useCallback(() => {
        const newValue = typeof value === 'undefined' ? min : value + 1;
        onChange === null || onChange === void 0 ? void 0 : onChange(max ? Math.min(newValue, max) : newValue);
    }, [onChange, max, min, value]);
    const { focusBlurEvents } = useFocusTriggers({ name: name, onFocus, onBlur });
    /**
     * the input.type="text" is caused by accessibility issues with type="number" as described at https://technology.blog.gov.uk/2020/02/24/why-the-gov-uk-design-system-team-changed-the-input-type-for-numbers/
     */
    return (React.createElement(NumberStepperContainer, { ...props, ...focusBlurEvents, ref: ref },
        React.createElement(NumberStepperButton, { ariaLabel: ariaMinusButtonLabel, onClick: handleDecrement, disabled: !canDecrement, error: hasError, icon: Icons.minus }),
        React.createElement(StyledInput, { id: id, ...aria({
                disabled,
                live: 'polite',
                ...inputProps,
            }), value: value !== undefined && value !== null ? value.toString() : '', name: name, onChange: handleChange, disabled: disabled, error: hasError, errorMessage: errorMessage || (typeof error === 'string' && error !== '' ? error : undefined), type: "text", min: min, max: max, pattern: "\\d*", inputMode: "numeric" }),
        React.createElement(NumberStepperButton, { ariaLabel: ariaPlusButtonLabel, onClick: handleIncrement, disabled: !canIncrement, icon: Icons.plus, error: hasError }),
        hasLine && React.createElement(NumberStepperLine, { disabled: disabled, error: hasError })));
});
NumberStepper.displayName = 'NumberStepper';
export default NumberStepper;
const BLOCK_SIZE_MEDIUM = Spacings.xLarge;
const StyledInput = styled(Input)(({ theme, ...props }) => {
    const { enabled, error, disabled } = theme.numberStepper.states;
    // BLOCK_SIZE_MEDIUM + 2 * Spacings.xxSmall but overruled due to the bigger IconButton after the hitbox area updates
    const width = createCustomSpacing(2);
    return css(css `
      color: ${enabled.textColor};
      width: ${width};
      height: ${BLOCK_SIZE_MEDIUM};
      border: none;
      background-color: transparent;
      text-align: center;
      opacity: ${Opacities.full};
      box-shadow: none;
      padding-inline-start: 0;
      padding-inline-end: 0;

      &::-webkit-outer-spin-button,
      &::-webkit-inner-spin-button {
        /* display: none; <- Crashes Chrome on hover */
        -webkit-appearance: none;
        margin: 0; /* <-- Apparently some margin are still there even though it's hidden */
      }

      -moz-appearance: textfield; /* Firefox */

      &:focus {
        outline: none;
        box-shadow: none;
      }
    `, props.errorMessage &&
        css `
        color: ${error.textColor};
      `, props.disabled &&
        css `
        color: ${disabled.textColor};
        opacity: ${Opacities.low};
        user-select: none;

        ::selection {
          background: transparent; /* WebKit/Blink Browsers */
        }
        ::-moz-selection {
          background: transparent; /* Gecko Browsers */
        }
      `);
});
const NumberStepperLine = styled.div(({ theme, ...props }) => {
    const { enabled, error, disabled } = theme.numberStepper.states;
    return css(css `
      width: 100%;
      height: 1px;
      position: absolute;
      background-color: ${enabled.lineColor};
      inset-block-end: 0;
    `, props.error &&
        css `
        background-color: ${error.lineColor};
      `, props.disabled &&
        css `
        background-color: ${disabled.lineColor};
      `);
});
const NumberStepperContainer = styled.div `
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
`;
